import React from 'react';
import type { MealBreakConfig } from '@/types';
import { FixedFeeTimeline } from '@/components/config_forms/shared/help-graphics/FixedFeeTimeline';
import { NumberInput } from '@/components/ui';
import { FeeScheduleInput } from '../sections/fixed_fee/FeeScheduleInput';

interface FixedFeeTimelineInjectableParams {
  mealBreakConfig: MealBreakConfig;
  onMealConfigChange?: (updates: Partial<MealBreakConfig>) => void;
  fixedFeeStructureMode?: 'single' | 'schedule' | null;
  onFixedFeeStructureModeChange?: (mode: 'single' | 'schedule') => void;
}

export function buildFixedFeeTimelineInjectable({
  mealBreakConfig,
  onMealConfigChange,
  fixedFeeStructureMode,
  onFixedFeeStructureModeChange,
}: FixedFeeTimelineInjectableParams): {
  injectables: Record<string, React.ReactNode>;
} {
  const handleSingleFeeChange = (newFee: number | null) => {
    if (!onMealConfigChange) return;
    onMealConfigChange({
      fixedFeeAmount: newFee,
      fixedFeeSchedule: newFee !== null ? [{ fee_amount: newFee }] : null,
    });
  };

  const handleScheduleChange = (schedule: { fee_amount: number | null }[]) => {
    if (!onMealConfigChange) return;
    onMealConfigChange({
      fixedFeeSchedule: schedule,
      fixedFeeAmount: schedule.length > 0 ? schedule[0].fee_amount : null,
    });
  };

  const element = React.createElement(FixedFeeTimeline, {
    feeSchedule: mealBreakConfig.fixedFeeSchedule ?? [],
    deadlineHours: mealBreakConfig.mealDeadlineHours ?? 5,
    recurringDeadline: mealBreakConfig.recurringMealDeadline ?? false,
    mealTriggerMode: mealBreakConfig.mealTriggerMode ?? 'deadline',
    requiredBreaks: mealBreakConfig.requiredBreaks ?? 2,
    scalingEnabled: (mealBreakConfig.additionalBreakIntervalHours ?? 0) > 0,
    fixedFeeStructureMode: fixedFeeStructureMode ?? undefined,
    onFixedFeeStructureModeChange,
    ...(onMealConfigChange ? {
      onFeeScheduleChange: handleScheduleChange,
      controlSlotContent: React.createElement(FeeScheduleInput, {
        fixedFeeSchedule: mealBreakConfig.fixedFeeSchedule ?? [],
        onFeeScheduleChange: handleScheduleChange,
        columns: 3,
      }),
      singleControlSlotContent: React.createElement(
        'div',
        { className: 'flex items-center justify-end' },
        React.createElement(NumberInput, {
          id: 'fixed-fee-help-single',
          'aria-label': 'Fixed fee amount',
          className: 'w-24',
          size: 'xs',
          touchTarget: true,
          unit: '$',
          unitPosition: 'left',
          step: '0.01',
          min: '0.01',
          placeholder: '0.00',
          value: mealBreakConfig.fixedFeeAmount ?? '',
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            handleSingleFeeChange(value === '' ? null : parseFloat(value));
          },
        }),
      ),
    } : {}),
  });

  return {
    injectables: { fixed_fee_timeline: element },
  };
}
